import styles from "./loading.module.scss";
import React, { useEffect, useRef, useState } from "react";

/**
 *
 * Pull to refresh 컴포넌트
 * @param refreshFunction 새로고침 함수
 * @param component 기준 컴포넌트
 */
export const PullToRefresh = ({
  refreshFunction,
  element,
  parentElement,
  disable = false,
}: {
  refreshFunction: () => Promise<boolean>;
  element?: HTMLElement;
  parentElement?: HTMLElement;
  disable?: boolean;
}) => {
  const disableRefreshRef = useRef(disable);
  const [disableRefresh, setDisableRefresh] = useState(disable);

  // 스타트 Y 좌표
  const startY = useRef(0);

  // 움직임 Y 좌표
  const moveY = useRef(0);

  // 새로고침 여부
  const isRefreshing = useRef(false);

  // 방향
  const direction = useRef("");

  // ref
  const ref = useRef<HTMLDivElement>(null);

  // 초기 X 좌표
  const ininitalX = useRef<number | null>(null);

  // 초기 Y 좌표
  const initialY = useRef<number | null>(null);

  // 터치 종료 시간
  const touchEndTime = useRef(0);

  // 타겟 엘리먼트
  const targetElement = useRef<HTMLElement | null>(null);

  // 부모 엘리멘트
  const parentElementRef = useRef<HTMLElement | null>(null);

  // 풀 임계값
  const pullThreshold = 200;

  // 풀 최대값(위치)
  const pullMax = 150;

  // reset Timer
  const resetTimer = useRef<NodeJS.Timeout | null>(null);

  // 최초 터치
  const initTouch = (e: any) => {
    ininitalX.current = e.touches ? e.touches[0].clientX : e.clientX;
    initialY.current = e.touches ? e.touches[0].clientY : e.clientY;
  };

  // 스와이프 방향
  const swipeDirection = (e: any) => {
    if (ininitalX.current !== null && initialY.current !== null && !disableRefreshRef.current) {
      const currentX = e.touches ? e.touches[0].clientX : e.clientX,
        currentY = e.touches ? e.touches[0].clientY : e.clientY;
      const diffX = ininitalX.current - currentX;
      const diffY = initialY.current - currentY;
      let dir = "";
      if (Math.abs(diffX) > Math.abs(diffY)) {
        dir = diffX > 0 ? "to left" : "to right";
      } else {
        dir = diffY > 0 ? "to top" : "to bottom";
      }
      direction.current = dir;
      ininitalX.current = null;
      initialY.current = null;
    }
  };

  // 새로고침 액션
  const simulateRefreshAction = async () => {
    if (!ref.current) return;
    ref.current.classList.remove(styles["pulling"]);
    ref.current.classList.add(styles["refreshing"]);
    await refreshFunction();
    resetTimer.current = setTimeout(() => {
      reset();
    }, 1000);
  };

  // 터치 시작
  const handleTouchStart = (e: any) => {
    resetTimer.current && clearTimeout(resetTimer.current);
    reset();

    // 타겟이 없거나, disable 상태일 경우 리턴
    if (
      !targetElement.current ||
      !ref.current ||
      disableRefreshRef.current ||
      isRefreshing.current
    ) {
      return;
    }

    // 컨텐츠가 스크롤 가능한 상태인지
    const isScrollable =
      targetElement?.current.clientHeight > parentElementRef?.current.clientHeight;
    // 터치 종료 시점 이후 너무 짧으면 리턴
    if (isScrollable) {
      if (Date.now() - touchEndTime?.current < 800) {
        return;
      }
    } else {
      if (Date.now() - touchEndTime?.current < 300) {
        return;
      }
    }

    // 스크롤이 맨 위에 있을 경우만 터치 이벤트 발생
    if (parentElementRef.current?.scrollTop === 0 && !isRefreshing.current) {
      startY.current = e.touches[0].clientY;
      initTouch(e);
    }
  };

  const handleTouchMove = (e: any) => {
    // 타겟이 없거나, disable 상태일 경우 리턴
    if (
      !targetElement.current ||
      !ref.current ||
      disableRefreshRef.current ||
      isRefreshing.current
    ) {
      return;
    }
    if (isRefreshing.current || parentElementRef.current?.scrollTop > 0 || startY.current === 0) {
      return;
    }

    // 스와이프 방향
    swipeDirection(e);

    // 터치 Y 좌표
    const touchY = e.touches[0].clientY;

    // 시작 Y 좌표와 현재 Y 좌표의 차이
    const diffY = touchY - startY.current;

    // 방향이 아래로이고, 차이가 0보다 크면 moveY에 값을 할당
    if (direction.current === "to bottom" && diffY > 0) {
      moveY.current = diffY > pullThreshold ? pullThreshold : diffY;
      targetElement.current.style.overflow = "hidden"; // 스크롤 방지
      ref.current.classList.add(styles["pulling"]);

      const transformY = moveY.current > pullMax ? pullMax : moveY.current;
      ref.current.style.transform = `translateY(${transformY}px)`;
      targetElement.current.style.transform = `translateY(${transformY}px)`;
    }
  };

  // 터치 종료
  const handleTouchEnd = (e: any) => {
    touchEndTime.current = Date.now();

    // 타겟이 없거나, disable 상태일 경우 리턴
    if (
      !targetElement.current ||
      !ref.current ||
      disableRefreshRef.current ||
      isRefreshing.current
    ) {
      return;
    }
    // 풀 임계값에 도달하고, 방향이 아래로이고, 새로고침 중이 아니며, 스크롤 중이 아닐 경우
    if (
      moveY.current === pullThreshold &&
      direction.current === "to bottom" &&
      !isRefreshing.current
    ) {
      isRefreshing.current = true;
      simulateRefreshAction();
    } else {
      reset();
    }
  };

  // 리셋
  const reset = () => {
    isRefreshing.current = false;
    moveY.current = 0;
    startY.current = 0;
    direction.current = "";
    ininitalX.current = null;
    initialY.current = null;

    if (!targetElement.current || !ref.current) return;
    ref.current.style.transform = `translateY(${0}px)`;
    targetElement.current.style.transform = `translateY(${0}px)`;
    ref.current.classList.remove(styles["refreshing"]);
    ref.current.classList.remove(styles["pulling"]);
    targetElement.current.style.overflow = "";
  };

  // 타겟 요소 할당
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = `${pullMax}px`;
      ref.current.style.marginTop = `-${pullMax}px`;
      if (element) {
        targetElement.current = element as HTMLElement;
      } else {
        targetElement.current = ref.current.nextElementSibling as HTMLElement;
      }
      if (parentElement) {
        parentElementRef.current = parentElement as HTMLElement;
      } else {
        parentElementRef.current = targetElement.current?.parentElement as HTMLElement;
      }
    }
  }, [ref.current, element, parentElement]);

  useEffect(() => {
    setDisableRefresh(disable);
    disableRefreshRef.current = disable;
  }, [disable]);

  // 이벤트 바인딩
  useEffect(() => {
    if (parentElementRef?.current) {
      parentElementRef.current.addEventListener("touchstart", handleTouchStart);
      parentElementRef.current.addEventListener("touchmove", handleTouchMove);
      parentElementRef.current.addEventListener("touchend", handleTouchEnd);
    }
    return () => {
      parentElementRef?.current?.removeEventListener("touchstart", handleTouchStart);
      parentElementRef?.current?.removeEventListener("touchmove", handleTouchMove);
      parentElementRef?.current?.removeEventListener("touchend", handleTouchEnd);
    };
  }, [parentElementRef.current]);

  return (
    <div ref={ref} className={styles["pull-to-refresh"]}>
      <div className={styles["spinner"]}>
        <span></span>
      </div>
    </div>
  );
};

export default PullToRefresh;
